import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clearAuth, getUser } from '../utils/auth';
import api from '../utils/api';
import {
  HomeIcon, UsersIcon, CurrencyDollarIcon, ChartBarIcon, EnvelopeIcon,
  ShieldCheckIcon, Cog6ToothIcon, BuildingOfficeIcon,
  AcademicCapIcon, CalendarIcon, UserGroupIcon,
  BookOpenIcon, BellIcon, ArrowRightOnRectangleIcon, GlobeAltIcon, BanknotesIcon,
  Bars3Icon, XMarkIcon, ChartPieIcon, MegaphoneIcon,
  SwatchIcon, TagIcon, ClockIcon, UserIcon, DocumentCheckIcon, IdentificationIcon, CreditCardIcon, ClipboardDocumentListIcon, QrCodeIcon, BeakerIcon
} from '@heroicons/react/24/outline';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from '../i18n/LanguageContext';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealDark: '#0087A8',
  bg: '#F0F7FA', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF',
};

const NAV = {
  superadmin: [
    { label: 'Overview', path: '/superadmin', icon: HomeIcon },
    { label: 'Partners', path: '/superadmin/tenants', icon: BuildingOfficeIcon },
    { label: 'Students', path: '/superadmin/students', icon: AcademicCapIcon },
    { label: 'Revenue', path: '/superadmin/revenue', icon: CurrencyDollarIcon },
    { label: 'API Usage', path: '/superadmin/api-usage', icon: ChartBarIcon },
    { label: 'API Costs', path: '/superadmin/api-costs', icon: CurrencyDollarIcon },
    { label: 'Email Logs', path: '/superadmin/email-logs', icon: EnvelopeIcon },
    { label: 'Websites', path: '/superadmin/websites', icon: BuildingOfficeIcon },
    { label: 'Security', path: '/superadmin/security', icon: ShieldCheckIcon },
    { label: 'Settings', path: '/superadmin/settings', icon: Cog6ToothIcon },
    { label: 'Subscriptions', path: '/superadmin/subscriptions', icon: BanknotesIcon },
    { label: 'Bank Settings', path: '/superadmin/bank-settings', icon: CreditCardIcon },
    { label: 'Cert Signature', path: '/superadmin/certificates', icon: DocumentCheckIcon },
    { label: 'Lesson Bank', path: '/superadmin/lesson-bank', icon: BookOpenIcon },
    { label: 'Mock Tests', path: '/superadmin/mock-tests', icon: BeakerIcon },
    { label: 'Partner Pitch', path: '/speakiq-pitch.html', icon: BookOpenIcon, external: true },
  ],
  tenant_admin: [
    { label: 'Dashboard', path: '/admin', icon: HomeIcon },
    { label: 'Students', path: '/admin/students', icon: AcademicCapIcon },
    { label: 'Bookings', path: '/admin/bookings', icon: CalendarIcon },
    { label: 'Groups', path: '/admin/groups', icon: UserGroupIcon },
    { label: 'Lesson Bank', path: '/admin/lesson-bank', icon: BookOpenIcon },
    { label: 'Payments', path: '/admin/payments', icon: CurrencyDollarIcon },
    { label: 'Payouts', path: '/admin/payouts', icon: BanknotesIcon },
    { label: 'Billing', path: '/admin/billing', icon: CreditCardIcon },
    { label: 'Supply & Demand', path: '/admin/supply-demand', icon: ChartPieIcon },
    { label: 'Marketing', path: '/admin/marketing', icon: MegaphoneIcon },
    { label: 'Branding', path: '/admin/branding', icon: SwatchIcon },
    { label: 'Pricing', path: '/admin/pricing', icon: TagIcon },
    { label: 'Promotions', path: '/admin/promotions', icon: TagIcon },
    { label: 'Tutor Rates', path: '/admin/tutor-rates', icon: CurrencyDollarIcon },
    { label: 'Certificates', path: '/admin/certificates', icon: DocumentCheckIcon },
    { label: 'Registrations', path: '/admin/registrations', icon: UsersIcon },
    { label: 'Tutors', path: '/admin/tutors', icon: UserGroupIcon },
    { label: 'Attendance', path: '/admin/attendance', icon: ClipboardDocumentListIcon },
    { label: 'Check-In', path: '/admin/checkin', icon: QrCodeIcon },
    { label: 'Assessment', path: '/admin/assessment', icon: BeakerIcon },
    { label: 'Mock Tests', path: '/admin/mock-tests', icon: ClipboardDocumentListIcon },
    { label: 'My Website', path: '/admin/website', icon: BuildingOfficeIcon },
    { label: 'Settings', path: '/admin/settings', icon: Cog6ToothIcon },
  ],
  tutor: [
    { label: 'Dashboard', path: '/tutor', icon: HomeIcon },
    { label: 'My Schedule', path: '/tutor/schedule', icon: CalendarIcon },
    { label: 'My Earnings', path: '/tutor/earnings', icon: CurrencyDollarIcon },
    { label: 'Availability', path: '/tutor/availability', icon: ClockIcon },
    { label: 'My Students', path: '/tutor/students', icon: UsersIcon },
    { label: 'Mock Tests', path: '/tutor/mock-tests', icon: BeakerIcon },
    { label: 'My Profile', path: '/tutor/profile', icon: UserIcon },
    { label: 'My ID Card', path: '/tutor/my-id', icon: IdentificationIcon },
    { label: 'Payment Settings', path: '/tutor/payment-settings', icon: BanknotesIcon },
  ],
};

export default function Layout({ children, role, tenantColor, tenantName, tenantLogo, tenantTier }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, lang, setLanguage } = useTranslation();
  const [showLangMenu, setShowLangMenu] = React.useState(false);

  const navLabelKeys = {
    'Overview': 'nav_overview',
    'Students': 'nav_students',
    'Tutors': 'nav_tutors',
    'Payments': 'nav_payments',
    'Payouts': 'nav_payouts',
    'Branding': 'nav_branding',
    'Bookings': 'nav_bookings',
    'Supply & Demand': 'nav_supply_demand',
    'Marketing': 'nav_marketing',
    'Settings': 'nav_settings',
    'Pricing': 'nav_pricing',
    'Certificates': 'nav_certificates',
    'Website': 'nav_website',
    'My Website': 'nav_website',
    'Registrations': 'nav_registrations',
    'Dashboard': 'nav_dashboard',
    'My Lessons': 'nav_my_lessons',
    'Placement Test': 'nav_placement_test',
    'Vocabulary': 'nav_vocabulary',
    'My Certificates': 'nav_my_certificates',
    'Book a Tutor': 'nav_book_tutor',
    'Homework': 'nav_homework',
    'My ID Card': 'nav_my_id',
    'Payment Settings': 'nav_payment_settings',
    'Programmes': 'nav_programmes',
    'My Students': 'nav_my_students',
    'My Earnings': 'nav_earnings',
    'Availability': 'nav_availability',
    'Earnings': 'nav_earnings',
    'My Schedule': 'nav_my_lessons',
    'My Profile': 'nav_settings',
  };

  const sidebarColor = role === 'superadmin' ? C.navy : (tenantColor || C.navy);
  const navItems = NAV[role] || [];
  const displayName = role === 'superadmin' ? 'Super Admin' : (tenantName || 'Admin');

  // Partner-wide overdue-invoice banner (shown on every page except Billing).
  const [overdueCount, setOverdueCount] = useState(0);
  useEffect(() => {
    if (role !== 'tenant_admin') return;
    api.get('/api/admin/billing/outstanding')
      .then(r => setOverdueCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => {});
  }, [role]);
  const showOverdueBar = role === 'tenant_admin' && overdueCount > 0 && location.pathname !== '/admin/billing';

  const handleLogout = () => { clearAuth(); navigate('/login'); };

  const isActive = (path) => {
    if (path === '/superadmin' || path === '/admin' || path === '/tutor') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const Sidebar = () => (
    <div className={`sidebar${mobileOpen ? ' mobile-open' : ''}`} style={{ background: sidebarColor, display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <div style={{ padding: '24px 20px', borderBottom: 'rgba(255,255,255,0.1) 1px solid' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {tenantLogo ? (
            <img src={tenantLogo} alt={displayName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                {role === 'superadmin' ? 'S' : (tenantName?.[0] || 'A')}
              </span>
            </div>
          )}
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
              {role === 'superadmin' ? 'SpeakIQ' : (tenantName || 'School')}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 500 }}>
              {role === 'superadmin' ? 'Super Admin' : role === 'tutor' ? user?.first_name || 'Tutor' : role === 'student' ? 'Student Portal' : t('nav_partner_panel')}
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 12px' }}>
        {navItems.map(({ label, path, icon: Icon, external }) => {
          const navStyle = {
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 8, marginBottom: 2,
            color: (!external && isActive(path)) ? '#fff' : 'rgba(255,255,255,0.65)',
            background: (!external && isActive(path)) ? 'rgba(255,255,255,0.15)' : 'transparent',
            textDecoration: 'none', fontSize: 14, fontWeight: (!external && isActive(path)) ? 600 : 400,
            transition: 'all 0.15s',
          };
          const hoverIn = e => { if (external || !isActive(path)) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; };
          const hoverOut = e => { if (external || !isActive(path)) e.currentTarget.style.background = 'transparent'; };
          const inner = (<><Icon style={{ width: 18, height: 18, flexShrink: 0 }} />{t(navLabelKeys[label] || label)}</>);
          return external ? (
            <a key={path} href={path} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}
              style={navStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>{inner}</a>
          ) : (
            <Link key={path} to={path} onClick={() => setMobileOpen(false)}
              style={navStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>{inner}</Link>
          );
        })}
      </nav>

      {/* Bottom user info */}
      <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>
              {(user?.first_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
            </span>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.first_name || user?.email || 'Admin'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
              {user?.email || ''}
            </div>
          </div>
        </div>
        <button onClick={handleLogout} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8,
          padding: '8px 12px', color: 'rgba(255,255,255,0.7)', fontSize: 13,
          cursor: 'pointer', transition: 'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
        >
          <ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />
          {t('btn_logout')}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
      <Toaster position="top-right" />
      {/* Mobile overlay — closes sidebar when tapped */}
      <div
        className={`mobile-overlay${mobileOpen ? ' mobile-open' : ''}`}
        onClick={() => setMobileOpen(false)}
      />
      <Sidebar />
      <div className="main-with-sidebar" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <header style={{
          background: '#fff', borderBottom: `1px solid ${C.border}`,
          padding: '0 32px', height: 64, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Hamburger — mobile only */}
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8 }}
            >
              {mobileOpen
                ? <XMarkIcon style={{ width: 24, height: 24, color: C.dark }} />
                : <Bars3Icon style={{ width: 24, height: 24, color: C.dark }} />}
            </button>
            <div>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.dark }}>
                {navItems.find(n => isActive(n.path))?.label || 'Dashboard'}
              </h1>
              <p style={{ margin: 0, fontSize: 12, color: C.grey }} className="hide-xs">
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Language picker */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowLangMenu(m => !m)}
                style={{ display:'flex', alignItems:'center', gap:6, background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:13, fontWeight:600, color:C.dark }}
              >
                <GlobeAltIcon style={{ width:16, height:16, color:C.grey }} />
                {lang === 'ru' ? '🇷🇺 RU' : lang === 'uz' ? '🇺🇿 UZ' : '🇬🇧 EN'}
              </button>
              {showLangMenu && (
                <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:200, minWidth:140, overflow:'hidden' }}>
                  {[
                    { code:'en', flag:'🇬🇧', label:'English' },
                    { code:'ru', flag:'🇷🇺', label:'Русский' },
                    { code:'uz', flag:'🇺🇿', label:"O'zbekcha" },
                  ].map(({ code, flag, label }) => (
                    <button key={code} onClick={() => { setLanguage(code); setShowLangMenu(false); }} style={{
                      display:'block', width:'100%', textAlign:'left', padding:'10px 16px',
                      background: lang === code ? '#F0F7FA' : 'transparent',
                      border:'none', cursor:'pointer', fontSize:14,
                      fontWeight: lang === code ? 700 : 400, color: lang === code ? C.teal : C.dark,
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      {flag} {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 8 }}>
              <BellIcon style={{ width: 22, height: 22, color: C.grey }} />
              <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: C.error, borderRadius: '50%' }} />
            </button>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: sidebarColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                {(user?.first_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: 'clamp(12px, 3vw, 32px)', overflowX: 'hidden' }}>
          {showOverdueBar && (
            <div onClick={() => navigate('/admin/billing')}
              style={{ background: '#FEF2F2', border: '2px solid #F87171', borderRadius: 12, padding: '12px 18px', marginBottom: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>🔒</span>
              <span style={{ color: '#991B1B', fontWeight: 700, fontSize: 14 }}>
                You have {overdueCount} overdue subscription invoice{overdueCount !== 1 ? 's' : ''}.
              </span>
              <span style={{ color: '#B91C1C', fontSize: 13, textDecoration: 'underline' }}>Pay now →</span>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
